import { useContext, useMemo, useState } from 'react'
import * as Sentry from '@sentry/browser'
import { AppContext } from 'contexts/AppProvider'
import { useLocalStorage } from 'react-use'
import { filterAdjustmentsPromoDiscount } from 'helpers/cart'
import {
  useBuffetOrderFpWithPromotionQuery,
  useBuffetOrderHhWithPromotionQuery,
  type BuffetOrderFpWithPromotionQuery,
  type BuffetOrderHhWithPromotionQuery,
  OrderStatusEnum,
} from 'graphQL/dinein/generate/operations'
import { dineInClient } from 'variables/services'
import useBuffetOrder from './useBuffetOrder'
import { OrderLineItemType } from 'services/hooks/dine-in/useGetGlobalCart'
import { ModalEnum } from 'components/buffet/BuffetModals'
import {
  buffetSelector,
  onCurrentPackageChange,
  resetBuffetModal,
  setBuffetModal,
  setBuffetScreen,
} from 'store/slices/buffetSlice'
import { useAppDispatch } from 'store'
import { useSelector } from 'react-redux'
import useValidateCurrentOrder, { OrderInvalidEnum } from 'hooks/buffet/useValidateCurrentOrder'
import useSubscribeDineInOrderV2 from 'hooks/useSubscribeDineInOrderV2'
import { isProduction } from 'variables/environment'
import { ScreenEnum } from 'components/buffet/BuffetOverlayPage'
import useBuffetOrderManagement from './useBuffetOrderManagement'

type BuffetOrderWithPromotion =
  | BuffetOrderFpWithPromotionQuery['orderAndFPMemberCoupons']['order']
  | BuffetOrderHhWithPromotionQuery['orderAndHHMemberCoupons']['order']

const useBuffetGlobalCart = () => {
  const { brand, isFoodPassion } = useContext(AppContext)
  const [orderIDLocal] = useLocalStorage<number>('orderID')
  const buffet = useSelector(buffetSelector)
  const dispatch = useAppDispatch()
  const [globalCart, setGlobalCart] = useState<{ lineItems: OrderLineItemType[] }>({ lineItems: [] })
  const [isLoadCompleted, setIsLoadCompleted] = useState(false)
  const { currentOrder, setCurrentOrder, buffetItems, buffetPackages, lineItems } = useBuffetOrder()
  const { validateCurrentOrder } = useValidateCurrentOrder()
  const { refreshBuffetOrder } = useBuffetOrderManagement()
  const skipRequest = !orderIDLocal || !brand?.id

  const handleOrderWithPromotion = async (order: BuffetOrderWithPromotion) => {
    const isSubscribeOrder = ![OrderStatusEnum.Completed, OrderStatusEnum.Cancelled].includes(order.status)
    if (isSubscribeOrder) {
      await subscribeOrder().catch((error) => {
        isProduction &&
          Sentry.captureException(
            new Error(`Error from subscribe Buffet global cart ${orderIDLocal}: ${JSON.stringify(error)}`)
          )
      })
    }
    handleOrderResult(order)
    const validateResult = validateCurrentOrder(buffet.currentOrder, false)
    if (
      validateResult.invalid === OrderInvalidEnum.OwnerNeedToCheckout &&
      buffet.modal.type !== ModalEnum.ConfirmCheckout
    ) {
      dispatch(setBuffetModal({ type: ModalEnum.ContinueCheckout, isProcessing: false }))
    }
    setIsLoadCompleted(true)
  }

  const resBuffetOrderFpWithPromotion = useBuffetOrderFpWithPromotionQuery({
    client: dineInClient,
    fetchPolicy: 'no-cache',
    skip: skipRequest || !isFoodPassion,
    variables: {
      orderID: Number(orderIDLocal),
    },
    onCompleted: ({ orderAndFPMemberCoupons }) => {
      const { order } = orderAndFPMemberCoupons
      handleOrderWithPromotion(order)
    },
  })

  const resBuffetOrderHhWithPromotion = useBuffetOrderHhWithPromotionQuery({
    client: dineInClient,
    fetchPolicy: 'no-cache',
    skip: skipRequest || isFoodPassion,
    variables: {
      orderID: Number(orderIDLocal),
    },
    onCompleted: ({ orderAndHHMemberCoupons }) => {
      const { order } = orderAndHHMemberCoupons
      handleOrderWithPromotion(order)
    },
  })

  const refreshBuffetOrderAndPromotion = async () => {
    if (window.document.hidden || !isLoadCompleted) {
      return
    }

    try {
      if (isFoodPassion) {
        const result = await resBuffetOrderFpWithPromotion.refetch()
        if (result.data?.orderAndFPMemberCoupons) {
          handleOrderResult(result.data.orderAndFPMemberCoupons.order)
        } else {
          setBuffetScreen({ type: ScreenEnum.TableIsNotAvailable, isProcessing: false })
        }
      } else {
        const result = await resBuffetOrderHhWithPromotion.refetch()
        if (result.data?.orderAndHHMemberCoupons) {
          handleOrderResult(result.data.orderAndHHMemberCoupons.order)
        } else {
          setBuffetScreen({ type: ScreenEnum.TableIsNotAvailable, isProcessing: false })
        }
      }
    } catch (error) {
      const hookName = isFoodPassion ? 'BuffetOrderFpWithPromotionQuery' : 'BuffetOrderHhWithPromotionQuery'
      isProduction && Sentry.captureException(new Error(`Buffet error from ${hookName} : ${JSON.stringify(error)}`))
    }
  }

  const { subscribeOrder, unSubscribeOrder } = useSubscribeDineInOrderV2({
    orderID: Number(orderIDLocal),
    onOrderUpdate: refreshBuffetOrderAndPromotion,
    onFirebaseError: async () => {
      await refreshBuffetOrder()
    },
  })

  const resOrderWithPromotionData = isFoodPassion
    ? resBuffetOrderFpWithPromotion.data?.orderAndFPMemberCoupons
    : resBuffetOrderHhWithPromotion.data?.orderAndHHMemberCoupons

  const summaryGlobalCart = useMemo(() => {
    if (resOrderWithPromotionData?.order) {
      const { lineItemTotalSatangs, adjustments, totalSatangs } = resOrderWithPromotionData.order
      return {
        lineItemTotalSatangs,
        adjustments: filterAdjustmentsPromoDiscount(adjustments),
        totalSatangs,
      }
    } else {
      return null
    }
  }, [resOrderWithPromotionData])

  const isHavePromoDiscount = summaryGlobalCart && summaryGlobalCart.adjustments.length ? true : false

  const handleOrderResult = (order: BuffetOrderWithPromotion) => {
    setCurrentOrder(order)
    setGlobalCart(order)
    dispatch(onCurrentPackageChange(order.buffetPackage))
    if (buffet.modal.type === ModalEnum.CallingStaff && order.staffCallingReason === null) {
      dispatch(resetBuffetModal())
    }
  }

  return {
    isHavePromoDiscount,
    summaryGlobalCart,
    globalCart,
    currentOrder,
    buffetItems,
    buffetPackages,
    lineItems,
    unSubscribeOrder,
    isLoadCompleted,
    refreshBuffetOrder,
    refreshBuffetOrderAndPromotion,
  }
}

export default useBuffetGlobalCart
